import loadable from '@loadable/component';
import { toPercentage } from '@/utilities/formatting';
import ReportSection from '@/components/ReportSection';
import ReportSubSection from '@/components/ReportSubSection';
import ReportSectionHeader from '@/components/ReportSectionHeader';
import {
  PORTFOLIO_STATIC_COMPANY,
  PORTFOLIO_PAST_ROE,
  PORTFOLIO_PAST_ROA,
  PORTFOLIO_PAST_ROCE,
} from '@/constants/feedbackSections';
import { PORTFOLIO_DOWNLOAD_PAST_PERFORMANCE } from '@/constants/downloadSections';
import { getPortfolioInfographicDownloadUrl } from '@utilities/urlGenerator';
import DeferRenderUntilIntersecting from '@/components/DeferRenderUntilIntersecting';
const NoData = loadable(() => import('@/components/NoData'), {
  resolveComponent: (component) => component.NoData,
});
const ChartActionsContainer = loadable(
  () => import('@/components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActionsContainer,
  }
);
const ROAGauge = loadable(() => import('../ROAGauge'), { ssr: false });
const ROEGauge = loadable(() => import('../ROEGauge'), { ssr: false });
const ROCEGauge = loadable(() => import('../ROCEGauge'), { ssr: false });

export interface PastProps {
  portfolioId: string;
  secretCode?: string;
  sectionNumber: number;
  marketName: string;
  portfolioName: string;
  ROA?: number;
  marketROA?: number;
  ROE?: number;
  marketROE?: number;
  ROCE?: number;
  netIncomeGrowth5y?: number;
  revenueGrowth5y?: number;
}

const calculateKeyMetric = (
  ROE: number,
  netIncomeGrowth5y?: number,
  revenueGrowth5y?: number
) => {
  if (netIncomeGrowth5y && netIncomeGrowth5y > 0) {
    return {
      keymetricTitle: 'Historical Annual Earnings Growth',
      value: toPercentage(netIncomeGrowth5y, 2),
    };
  } else if (revenueGrowth5y && revenueGrowth5y > 0) {
    return {
      keymetricTitle: 'Historical Annual Revenue Growth',
      value: toPercentage(revenueGrowth5y, 2),
    };
  } else if (ROE > 0) {
    return {
      keymetricTitle: 'Portfolio Return on Equity',
      value: toPercentage(ROE > 1 ? ROE : ROE, 2),
    };
  } else {
    return {};
  }
};

const PastPerfomance = ({
  portfolioId,
  secretCode = '',
  sectionNumber,
  marketName,
  portfolioName,
  ROE,
  marketROE = 0,
  ROA = 0,
  marketROA = 0,
  ROCE = 0,
}: PastProps) => (
  <ReportSection sectionNumber={sectionNumber}>
    <ReportSectionHeader
      title="Past Performance"
      sectionDescription="The average performance ratios and growth over the past 5 years"
      keyMetricTitle={
        calculateKeyMetric(ROE as number, ROA, ROCE).keymetricTitle
      }
      keyMetric={calculateKeyMetric(ROE as number, ROA, ROCE).value}
    />
    <ReportSubSection
      title="Return on Equity"
      trackingProps={{ subject: 'chart' }}
      body={
        typeof ROE === 'number' ? (
          <DeferRenderUntilIntersecting preRenderHeight={279}>
            {() => (
              <>
                <ROEGauge
                  portfolioROE={ROE}
                  portfolioName={portfolioName}
                  marketROE={marketROE || 0}
                  marketName={marketName}
                />
                <ChartActionsContainer
                  companyName={PORTFOLIO_STATIC_COMPANY}
                  feedbackLabel={PORTFOLIO_PAST_ROE}
                  exportDarkUrl={
                    getPortfolioInfographicDownloadUrl(
                      portfolioId,
                      PORTFOLIO_DOWNLOAD_PAST_PERFORMANCE,
                      secretCode || ''
                    ) || ''
                  }
                />
              </>
            )}
          </DeferRenderUntilIntersecting>
        ) : (
          <NoData />
        )
      }
    />
    <hr />
    <ReportSubSection
      title="Return on Assets"
      trackingProps={{ subject: 'chart' }}
      body={
        typeof ROA === 'number' ? (
          <DeferRenderUntilIntersecting preRenderHeight={279}>
            {() => (
              <>
                <ROAGauge
                  portfolioROA={ROA}
                  portfolioName={portfolioName}
                  marketROA={marketROA || 0}
                  marketName={marketName}
                />
                <ChartActionsContainer
                  companyName={PORTFOLIO_STATIC_COMPANY}
                  feedbackLabel={PORTFOLIO_PAST_ROA}
                  exportDarkUrl={
                    getPortfolioInfographicDownloadUrl(
                      portfolioId,
                      PORTFOLIO_DOWNLOAD_PAST_PERFORMANCE,
                      secretCode || ''
                    ) || ''
                  }
                />
              </>
            )}
          </DeferRenderUntilIntersecting>
        ) : (
          <NoData />
        )
      }
    />
    <hr />
    <ReportSubSection
      title="Return on Capital Employed"
      trackingProps={{ subject: 'chart' }}
      body={
        typeof ROCE === 'number' ? (
          <DeferRenderUntilIntersecting preRenderHeight={279}>
            {() => (
              <>
                <ROCEGauge portfolioName={portfolioName} ROCE={ROCE} />
                <ChartActionsContainer
                  companyName={PORTFOLIO_STATIC_COMPANY}
                  feedbackLabel={PORTFOLIO_PAST_ROCE}
                  exportDarkUrl={
                    getPortfolioInfographicDownloadUrl(
                      portfolioId,
                      PORTFOLIO_DOWNLOAD_PAST_PERFORMANCE,
                      secretCode || ''
                    ) || ''
                  }
                />
              </>
            )}
          </DeferRenderUntilIntersecting>
        ) : (
          <NoData />
        )
      }
    />
  </ReportSection>
);

export default PastPerfomance;
